<template>
  <div :class="[multipage === true ? 'multi-page' : 'single-page', 'home-page']">
    <a-breadcrumb class="home-breadcrumb">
      <a-breadcrumb-item><router-link to="/home">主页</router-link></a-breadcrumb-item>
      <a-breadcrumb-item>系统主页</a-breadcrumb-item>
    </a-breadcrumb>
    <div class="not-menu-page home-content">
      <a-row :gutter="8" class="head-info">
        <a-card class="head-info-card">
          <a-col :xs="24" :lg="14" class="welcome-info">
            <div class="head-info-avatar">
              <img alt="头像" :src="avatar"
                   @error="$event.target.src.endsWith('/static/avatar/default.jpg') || ($event.target.src = 'static/avatar/default.jpg')" />
            </div>
            <div class="head-info-count">
              <div class="head-info-desc">
                <p>{{ user.deptName || '暂无部门' }} | {{ roleName }}</p>
              </div>
              <div class="head-info-time">
                上次登录时间：{{ user.lastLoginTime || '第一次访问系统' }}
              </div>
              <div class="head-info-welcome">{{ welcomeMessage }}</div>
            </div>
          </a-col>
          <a-col :xs="24" :lg="10" class="statistics-info">
            <a-row class="more-info">
              <a-col :span="8">
                <head-info title="今日IP" :content="todayIp" :center="false" :bordered="false" />
              </a-col>
              <a-col :span="8">
                <head-info title="今日访问" :content="todayVisitCount" :center="false" :bordered="false" />
              </a-col>
              <a-col :span="8">
                <head-info title="总访问量" :content="totalVisitCount" :center="false" />
              </a-col>
            </a-row>
          </a-col>
        </a-card>
      </a-row>
      <a-row :gutter="8" class="count-info">
        <a-col :span="24" class="visit-count-wrapper">
          <a-card title="近七日后台访问记录" class="visit-count">
            <a-spin :spinning="loading">
              <div class="visit-content">
                <div v-if="loadError" class="visit-error">
                  <a-alert type="error" :message="loadError" show-icon />
                  <a-button class="retry-button" @click="loadHomeData">重新加载</a-button>
                </div>
                <template v-else-if="!loading && series.length">
                  <apexchart type="bar" height="300" :options="chartOptions" :series="series" />
                  <div class="visit-detail">
                    <span v-if="isVisitEmpty">近七日暂无访问记录。</span>
                    鼠标悬浮在柱状图上，可查看当日“您”和“总数”的后台访问记录。
                  </div>
                </template>
              </div>
            </a-spin>
          </a-card>
        </a-col>
      </a-row>
    </div>
  </div>
</template>

<script>
import HeadInfo from '@/views/common/HeadInfo'
import { mapState } from 'vuex'
import moment from 'moment'
import ApexChart from 'vue-apexcharts'

export default {
  name: 'HomePage',
  components: { HeadInfo, apexchart: ApexChart },
  data() {
    return {
      todayIp: '-',
      todayVisitCount: '-',
      totalVisitCount: '-',
      welcomeMessage: '',
      loading: false,
      loadError: '',
      series: [],
      chartOptions: {
        chart: {
          toolbar: { show: false },
        },
        colors: ['#1890ff', '#00e396'],
        plotOptions: {
          bar: { horizontal: false, columnWidth: '35%' },
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ['transparent'] },
        xaxis: { categories: [] },
        yaxis: { min: 0, decimalsInFloat: 0, forceNiceScale: true },
        fill: { opacity: 1 },
        tooltip: {
          shared: true,
          intersect: false,
          hideEmptySeries: false,
          y: { formatter: (value) => `${value} 次` },
        },
      },
    }
  },
  computed: {
    ...mapState({
      multipage: (state) => state.setting.multipage,
      user: (state) => state.account.user || {},
      roles: (state) => state.account.roles || [],
    }),
    roleName() {
      return this.user.roleName || this.roles.join('，') || '暂无角色'
    },
    avatar() {
      const avatar = this.user.avatar || 'default.jpg'
      return /^(https?:\/\/|\/)/.test(avatar) ? avatar : `static/avatar/${avatar}`
    },
    isVisitEmpty() {
      return this.series.every((item) => item.data.every((count) => count === 0))
    },
  },
  methods: {
    loadHomeData() {
      if (this.loading) return
      const username = this.user.username
      if (!username) {
        this.loadError = '未获取到用户信息，请重新登录后重试'
        return
      }
      this.loading = true
      this.loadError = ''
      return this.$get(`index/${encodeURIComponent(username)}`, undefined, { hideError: true })
        .then((r) => {
          const result = r.data || {}
          if (result.code !== 200 || !result.data) {
            throw new Error('获取首页信息失败')
          }
          const data = result.data
          this.todayIp = data.todayIp != null ? data.todayIp : '-'
          this.todayVisitCount = data.todayVisitCount != null ? data.todayVisitCount : '-'
          this.totalVisitCount = data.totalVisitCount != null ? data.totalVisitCount : '-'
          this.updateVisitChart(data)
        })
        .catch(() => {
          this.todayIp = '-'
          this.todayVisitCount = '-'
          this.totalVisitCount = '-'
          this.loadError = '首页信息加载失败，请稍后重试'
        })
        .finally(() => {
          this.loading = false
        })
    },
    updateVisitChart(data) {
      const today = moment().startOf('day')
      const days = []
      for (let i = 6; i >= 0; i--) {
        days.push(today.clone().subtract(i, 'days').format('MM-DD'))
      }
      // 接口只返回有记录的日期，按日期对齐两组数据并补齐缺失的天数。
      const getCounts = (records) => days.map((day) => {
        const record = Array.isArray(records) && records.find((item) => item && item.days === day)
        const count = record ? Number(record.count) : 0
        return Number.isFinite(count) && count >= 0 ? count : 0
      })
      this.series = [
        { name: '您', data: getCounts(data.lastSevenUserVisitCount) },
        { name: '总数', data: getCounts(data.lastSevenVisitCount) },
      ]
      this.chartOptions = {
        ...this.chartOptions,
        xaxis: { ...this.chartOptions.xaxis, categories: days },
      }
    },
    welcome() {
      const hour = new Date().getHours()
      const time =
        hour < 6
          ? '早上好'
          : hour <= 11
            ? '上午好'
            : hour <= 13
              ? '中午好'
              : hour <= 18
                ? '下午好'
                : '晚上好'
      const name = this.user.username || this.user.name || ''
      return name ? `${time}，${name}，欢迎进入系统主页` : `${time}，欢迎进入系统主页`
    },
  },
  mounted() {
    this.welcomeMessage = this.welcome()
    this.loadHomeData()
  },
}
</script>

<style lang="less">
.home-page {
  .home-breadcrumb {
    padding: 14px 24px;
    background: #fff;
    border-left: 1px solid #e8e8e8;
    border-right: 1px solid #e8e8e8;
  }
  .home-content {
    padding: 16px 24px;
  }
  .head-info {
    margin-bottom: 0.5rem;
    .head-info-card {
      padding: 0.5rem;
      border-color: #f1f1f1;
      .ant-card-body {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
      }
      .welcome-info {
        display: flex;
        align-items: center;
      }
      .head-info-avatar {
        flex-shrink: 0;
        margin-right: 1rem;
        img {
          width: 5rem;
          height: 5rem;
          object-fit: cover;
          border-radius: 2px;
        }
      }
      .head-info-count {
        min-width: 0;
        .head-info-welcome {
          font-size: 0.875rem;
          margin-top: 0.2rem;
          margin-bottom: 0.1rem;
          font-weight: bold;
        }
        .head-info-desc {
          color: rgba(0, 0, 0, 0.45);
          font-size: 0.8rem;
          padding: 0.2rem 0;
          p {
            margin-bottom: 0;
          }
        }
        .head-info-time {
          color: rgba(0, 0, 0, 0.45);
          font-size: 0.8rem;
          padding: 0.2rem 0;
        }
      }
    }
  }
  .more-info {
    display: flex;
    align-items: center;
    height: 100%;
    justify-content: flex-end;
  }
  @media (max-width: 991px) {
    .statistics-info {
      margin-top: 1rem;
    }
  }
  .count-info {
    .visit-count-wrapper {
      padding-left: 0 !important;
      .visit-count {
        border-color: #f1f1f1;
        .ant-card-body {
          padding: 0.5rem 1rem;
        }
      }
    }
    .visit-content {
      min-height: 356px;
    }
    .visit-error {
      padding: 1rem 0;
      .retry-button {
        margin-top: 1rem;
      }
    }
    .visit-detail {
      margin-top: 0.5rem;
      padding: 0.75rem 1rem;
      background: rgba(0, 227, 150, 0.08);
      border-left: 3px solid #00e396;
    }
  }
}
</style>
